/**
 * Vérification d'identité avant une action sensible — un seul dialogue pour
 * toute l'application.
 *
 * Il s'ouvre quand l'API répond `step_up_required`, propose la clé d'accès de
 * l'appareil quand le compte en a une (décision 30), sinon demande le code de
 * l'application d'authentification (ou le mot de passe d'un compte sans double
 * authentification), puis laisse le client API rejouer la requête d'origine.
 * Un compte sans droit sensible peut recevoir un code par e-mail à la place
 * (24/10/2026) : même ordre qu'à la connexion, Face ID, code, e-mail.
 * L'écran appelant n'est ni démonté ni réinitialisé : sa saisie reste en place.
 * Le serveur retient la vérification 4 heures pour cette session.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { api, setStepUpHandler, setTokens, type StepUpDetails } from '../core/api.js';
import { useAuth } from '../core/auth.js';
import { useSignOut } from './SignOutGuard.js';
import { t } from '../core/i18n.js';
import { passkeyCancelled, passkeyErrorMessage, passkeyMethodName, passkeyStepUp, passkeysSupported, platformPasskeyAvailable } from '../core/passkeys.js';
import { Button, Field, Modal } from './ui.js';
import { Banner } from './ui/Feedback.js';

const TITLE = t('auth.verification.titre');
type Mode = 'totp' | 'recovery_code' | 'password' | 'webauthn' | 'email_code';

/** « 4 heures », « 30 minutes » : la durée annoncée est celle du serveur. */
export function stepUpValidityLabel(seconds = 4 * 60 * 60): string {
  const hours = seconds / 3600;
  if (Number.isInteger(hours)) return hours === 1 ? '1 heure' : `${hours} heures`;
  return `${Math.round(seconds / 60)} minutes`;
}

function StepUpDialog(props: {
  details: StepUpDetails | undefined;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const { me } = useAuth();
  // Envois en attente : annoncés avant de quitter la session (SignOutGuard).
  const signOut = useSignOut();
  // Le serveur dit quel facteur il attend ; à défaut, on le déduit du profil.
  const methods = props.details?.methods
    ?? (me?.mfa_enabled ? ['totp', 'recovery_code'] : me?.mfa_required ? [] : ['password']);
  const blocked = props.details?.blocked
    ?? (methods.length === 0 ? 'mfa_enrollment_required' : undefined);
  const secondFactor = methods.includes('totp');
  // Clé d'accès : proposée d'abord, si le compte en a une et si ce navigateur sait s'en servir.
  const passkey = methods.includes('webauthn') && passkeysSupported();
  // Nom du déverrouillage de cet appareil (« Face ID ou Touch ID », « Windows Hello »…).
  const [platform, setPlatform] = useState(false);
  useEffect(() => { void platformPasskeyAvailable().then(setPlatform); }, []);
  const methode = passkeyMethodName(platform);
  // Code par e-mail : seulement si le serveur le propose (compte sans droit sensible).
  const email = methods.includes('email_code');
  const typed: Mode | undefined = secondFactor ? 'totp' : methods.includes('password') ? 'password' : email ? 'email_code' : undefined;
  const [mode, setMode] = useState<Mode>(passkey ? 'webauthn' : typed ?? 'password');
  // Défi du code envoyé par e-mail et adresse masquée pour le retrouver.
  const [sent, setSent] = useState<{ challengeToken: string; emailHint: string; expiresInSeconds: number } | undefined>();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // Garde synchrone : deux appuis rapprochés n'envoient qu'une vérification
  // (chaque essai erroné compte dans le plafond du compte).
  const busy = useRef(false);

  // Le champ à remplir reçoit le focus (après celui que pose le dialogue),
  // à l'ouverture comme au changement de méthode.
  useEffect(() => {
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [mode]);

  const confirmWithPasskey = useCallback(async (): Promise<void> => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setError(undefined);
    try {
      setTokens(await passkeyStepUp());
      props.onDone();
    } catch (err) {
      if (!passkeyCancelled(err)) setError(passkeyErrorMessage(err));
      busy.current = false;
      setPending(false);
    }
  }, [props]);

  const sendEmail = useCallback(async (): Promise<void> => {
    setMode('email_code');
    setValue('');
    setError(undefined);
    try {
      setSent(await api<{ challengeToken: string; emailHint: string; expiresInSeconds: number }>('/auth/step-up/email/send', { method: 'POST', body: {} }));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);
  // Le code par e-mail est la seule saisie possible : il part dès l'ouverture.
  const autoSent = useRef(false);
  useEffect(() => {
    if (mode !== 'email_code' || sent || autoSent.current) return;
    autoSent.current = true;
    void sendEmail();
  }, [mode, sent, sendEmail]);

  const submit = useCallback(async (candidate: string): Promise<void> => {
    if (!candidate || busy.current) return;
    if (mode === 'email_code' && !sent) return;
    busy.current = true;
    setPending(true);
    setError(undefined);
    try {
      const result = mode === 'email_code'
        ? await api<{ accessToken: string }>('/auth/step-up/email', {
          method: 'POST', body: { challengeToken: sent!.challengeToken, code: candidate },
        })
        : await api<{ accessToken: string }>('/auth/step-up', {
          method: 'POST',
          body: { method: mode, value: candidate },
        });
      // Même session, jeton simplement rafraîchi : l'élévation est côté serveur.
      setTokens(result.accessToken);
      props.onDone();
    } catch (err) {
      setError((err as Error).message);
      if (mode !== 'password') setValue('');
      busy.current = false;
      setPending(false);
      input.current?.focus();
    }
  }, [mode, props, sent]);

  // Compte dont la seule vérification possible est une clé d'accès, sur un
  // navigateur qui ne la gère pas : le dire, sans champ inutile.
  if (!blocked && !passkey && !typed) {
    return (
      <Modal open title={TITLE} onClose={props.onClose}>
        <div className="stack">
          <Banner tone="danger">{t('compte.clesAcces.nonGere')}</Banner>
          <Button large onClick={props.onClose}>{t('commun.fermer')}</Button>
        </div>
      </Modal>
    );
  }

  if (blocked) {
    return (
      <Modal open title={TITLE} onClose={props.onClose}>
        <div className="stack">
          {blocked === 'password_missing' ? (
            <p>
              Votre compte n’a pas encore de mot de passe. Créez-en un avec « Mot de passe
              oublié » sur l’écran de connexion, puis relancez cette action.
            </p>
          ) : (
            <p>
              La double authentification est obligatoire pour votre compte. Reconnectez-vous
              pour la configurer, puis relancez cette action.
            </p>
          )}
          <Button large onClick={() => { props.onClose(); signOut(); }}>
            Revenir à la connexion
          </Button>
        </div>
      </Modal>
    );
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    void submit(value.trim());
  };
  const validity = stepUpValidityLabel(props.details?.validitySeconds);
  const note = (
    <p className="muted step-up__text step-up__note">
      Vous ne serez plus sollicité pendant {validity} sur cet appareil
      {props.details?.recentSeconds ? ' pour les autres actions sensibles' : ''}.
    </p>
  );
  const recent = props.details?.recentSeconds ? (
    <p className="step-up__text step-up__recent">{t('auth.verificationRecente')}</p>
  ) : null;

  if (mode === 'webauthn') {
    return (
      <Modal open title={TITLE} onClose={props.onClose}>
        <div className="stack" aria-busy={pending}>
          {recent}
          <p className="step-up__text">{t('auth.verification.cleTexte')}</p>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <Button large disabled={pending} onClick={() => void confirmWithPasskey()}>
            {pending ? t('auth.passkeyEnCours') : t('auth.verification.cleConfirmer', { methode })}
          </Button>
          {typed && typed !== 'email_code' ? (
            <Button variant="ghost" disabled={pending}
              onClick={() => { setMode(typed); setValue(''); setError(undefined); }}>
              {typed === 'totp' ? t('auth.verification.utiliserCode') : t('auth.verification.utiliserMotDePasse')}
            </Button>
          ) : null}
          {email ? (
            <Button variant="ghost" disabled={pending} onClick={() => void sendEmail()}>
              {t('auth.codeEmail.envoyer')}
            </Button>
          ) : null}
          {note}
        </div>
      </Modal>
    );
  }

  return (
    // ui-allow ui/no-form-in-modal: vérification d'identité, fenêtre autorisée (prompt maître §5 bis)
    <Modal open title={TITLE} onClose={props.onClose}>
      <form className="stack" onSubmit={onSubmit} aria-busy={pending}>
        {recent}
        <p className="step-up__text">
          {mode === 'totp'
            ? 'Saisissez le code de votre application d’authentification pour continuer.'
            : mode === 'recovery_code'
              ? 'Saisissez un de vos codes de secours. Chaque code ne sert qu’une fois.'
              : mode === 'email_code'
                ? (sent ? t('auth.codeEmail.envoye', { email: sent.emailHint, minutes: Math.max(1, Math.floor(sent.expiresInSeconds / 60)) }) : t('auth.codeEmail.envoi'))
                : 'Saisissez le mot de passe de votre compte pour continuer.'}
        </p>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {mode === 'password' ? (
          <>
            {/* Pour le gestionnaire de mots de passe : il sait quel compte proposer. */}
            <input type="text" name="username" autoComplete="username" readOnly hidden
              value={me?.username ?? me?.email ?? ''} />
            <Field label={t('auth.motDePasse')}>
              <input ref={input} type="password" name="password" autoComplete="current-password"
                required maxLength={256} enterKeyHint="done" value={value}
                onChange={(event) => setValue(event.target.value)} />
            </Field>
          </>
        ) : mode === 'totp' || mode === 'email_code' ? (
          <Field label={mode === 'email_code' ? t('auth.codeEmail.libelle') : t('auth.codeMfa')}>
            <input ref={input} type="text" name="one-time-code" autoComplete="one-time-code"
              inputMode="numeric" pattern="[0-9]*" maxLength={6} required enterKeyHint="done"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              className="step-up__code" value={value}
              onChange={(event) => setValue(event.target.value.replace(/\D/g, '').slice(0, 6))} />
          </Field>
        ) : (
          <Field label="Code de secours">
            <input ref={input} type="text" name="recovery-code" autoComplete="off"
              autoCapitalize="off" autoCorrect="off" spellCheck={false} required maxLength={64}
              enterKeyHint="done" value={value}
              onChange={(event) => setValue(event.target.value)} />
          </Field>
        )}
        <Button type="submit" large disabled={pending || !value.trim()}>
          {pending ? 'Vérification…' : t('commun.valider')}
        </Button>
        {secondFactor ? (
          <Button variant="ghost" disabled={pending}
            onClick={() => {
              setMode(mode === 'totp' ? 'recovery_code' : 'totp');
              setValue('');
              setError(undefined);
            }}>
            {mode === 'totp' ? 'Utiliser un code de secours' : 'Utiliser le code de l’application'}
          </Button>
        ) : null}
        {mode === 'email_code' ? (
          <Button variant="ghost" disabled={pending} onClick={() => void sendEmail()}>{t('auth.codeEmail.renvoyer')}</Button>
        ) : email ? (
          <Button variant="ghost" disabled={pending} onClick={() => void sendEmail()}>{t('auth.codeEmail.envoyer')}</Button>
        ) : null}
        {passkey ? (
          <Button variant="ghost" disabled={pending}
            onClick={() => { setMode('webauthn'); setValue(''); setError(undefined); }}>
            {t('auth.verification.utiliserCle')}
          </Button>
        ) : null}
        {note}
      </form>
    </Modal>
  );
}

interface Pending {
  details: StepUpDetails | undefined;
  promise: Promise<boolean>;
  settle: (verified: boolean) => void;
}

/** Branche le dialogue sur le client API. À monter une fois, autour de l'application. */
export function StepUpProvider({ children }: { children: ReactNode }): ReactNode {
  const { me } = useAuth();
  const [open, setOpen] = useState<Pending | undefined>();
  const current = useRef<Pending | undefined>(undefined);
  const support = Boolean(me?.support_session);

  useEffect(() => {
    // Une session d'assistance ne revérifie rien à la place du membre.
    if (!me || support) return undefined;
    return setStepUpHandler((details) => {
      // Des requêtes simultanées partagent le même dialogue.
      if (current.current) return current.current.promise;
      let settle: (verified: boolean) => void = () => {};
      const promise = new Promise<boolean>((resolve) => { settle = resolve; });
      current.current = { details, promise, settle };
      setOpen(current.current);
      return promise;
    });
  }, [me, support]);

  const finish = useCallback((verified: boolean) => {
    const pending = current.current;
    current.current = undefined;
    setOpen(undefined);
    pending?.settle(verified);
  }, []);
  // Démontage (déconnexion) : une demande en attente se termine, jamais suspendue.
  useEffect(() => () => finish(false), [finish]);
  const cancel = useCallback(() => finish(false), [finish]);
  const done = useCallback(() => finish(true), [finish]);

  return (
    <>
      {children}
      {open ? <StepUpDialog details={open.details} onClose={cancel} onDone={done} /> : null}
    </>
  );
}

